import { useTranslation } from "react-i18next";
import { getLoginUrl } from "~/lib/auth/auth";
import { DocumentIcon, ExternalLinkIcon } from "~/components/common/icons";
import type { AccessApplication, AccessApplicationLink } from "~/lib/types";

type Props = {
  merchantId: string;
  aas: AccessApplication[];
  linksByAaId: Record<string, AccessApplicationLink[]>;
  isAuthed: boolean;
};

export const MerchantForms = ({
  merchantId,
  aas,
  linksByAaId,
  isAuthed,
}: Props) => {
  const { t } = useTranslation();

  if (aas.length === 0) return null;

  return (
    <div>
      <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-text-full">
        <DocumentIcon className="h-5 w-5 text-accent" />
        {t("MerchantDetail.forms-heading")}
      </h2>

      {!isAuthed && (
        <div className="mt-5 rounded-xl border border-border-shell bg-surface-elevated p-5 text-center">
          <p className="text-text-medium">
            {t("MerchantDetail.forms-sign-in-prompt")}
          </p>
          <a
            href={getLoginUrl(`/merchants/${merchantId}`)}
            className="mt-3 inline-block rounded-lg bg-accent px-4 py-2 font-heading text-sm font-semibold uppercase tracking-wider text-ink hover:brightness-95"
          >
            {t("MerchantDetail.forms-sign-in-button")}
          </a>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {aas.map(aa => {
          const links = linksByAaId[aa.id] ?? [];
          return (
            <div
              key={aa.id}
              className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border-shell/60 dark:border-transparent bg-surface-blank p-4 shadow-simple ${
                isAuthed ? "" : "opacity-60"
              }`}
            >
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-text-full">
                  {aa.name}
                </h3>
                {aa.description && (
                  <p className="text-sm text-text-light">{aa.description}</p>
                )}
              </div>
              {isAuthed && links.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {links.map(link => (
                    <a
                      key={link.id}
                      href={link.short_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border-shell px-4 py-2 font-heading text-xs font-semibold uppercase tracking-wider text-text-medium transition hover:bg-surface-elevated hover:text-text-full"
                    >
                      <ExternalLinkIcon className="h-3.5 w-3.5" />
                      {links.length > 1 && link.link_description
                        ? link.link_description
                        : t("MerchantDetail.open-form")}
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
